import { Cancel, CheckCircle } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, CircularProgress, Tooltip } from '@mui/material';
import { Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { translate } from 'react-switch-lang';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { AccordionElement } from 'src/component/Accordion';
import ProductivityTable from 'src/component/ProductivityTable/ProductivityTable';
import ProductionCycleChargesTable from 'src/component/ProductionCycleChargesTable/ProductionCycleChargesTable';
import ProductionCycleProductsTable from 'src/component/ProductionCycleProductsTable/ProductionCycleProductsTable';
import { useProductionCycle } from 'src/hooks/useProductionCycle';
import { formatNumber } from 'src/utils/formatNumber';
import { parseProductionCyclePath } from 'src/utils/utilMethods';
import Breadcrumb from '../../../breadcrumb';

const ProductionCycleDetailPage = (props) => {
  const { productionCycle, loading, error } = useProductionCycle();
  const { id } = useParams<{ id: string }>();

  const safeParseLabel = (label: string) => {
    try {
      return parseProductionCyclePath(label).productionCycleLabel;
    } catch {
      return label || '-';
    }
  };

  const renderDetailRow = (label: string, value: any) => (
    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6c757d' }}>{label}</span>
      <span style={{ fontWeight: 500, color: '#212529' }}>{value ?? '-'}</span>
    </div>
  );

  if (loading) return (
    <Box textAlign="center" mt={5} py={5}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box mt={3} py={2}>
      <Alert severity={error.includes('404') ? 'info' : 'error'}>
        {error.includes('404') ? props.t('production_cycle.not_found') : error}
      </Alert>
    </Box>
  );

  if (!productionCycle) return (
    <Box mt={3} py={2}>
      <Alert severity="info">{props.t('production_cycle.not_found')}</Alert>
    </Box>
  );

  return (
    <Fragment>
      <Breadcrumb
        parent={props.t('production_cycle.list')}
        url={'settings/production-cycle'}
        title={props.t('production_cycle.detail')}
      />
      <Container fluid={true}>

        {/* Informations générales */}
        <Row>
          <Col sm="12">
            <Card className="shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 font-weight-bold">{props.t('production_cycle.information')}</h5>
                  <Tooltip title={props.t('production_cycle.edit_title')}>
                    <Link to={`${process.env.PUBLIC_URL}/settings/production-cycle/edit/${id}`}>
                      <EditOutlinedIcon
                        sx={{ cursor: 'pointer', color: '#2196f3', fontSize: 22, '&:hover': { color: '#1976d2' } }}
                      />
                    </Link>
                  </Tooltip>
                </div>
                {renderDetailRow(props.t('production_cycle.label'), safeParseLabel(productionCycle.label))}
                {renderDetailRow(props.t('production_cycle.mode'), productionCycle.mode)}
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6c757d' }}>{props.t('production_cycle.active')}</span>
                  <Tooltip title={productionCycle.active ? props.t('yes') : props.t('no')}>
                    {productionCycle.active ? (
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 22 }} />
                    ) : (
                      <Cancel sx={{ color: '#f44336', fontSize: 22 }} />
                    )}
                  </Tooltip>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Dates + Détails */}
        <Row className="mt-3">
          <Col md="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <h6 className="mb-3 text-muted font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  {props.t('production_cycle.dates')}
                </h6>
                {renderDetailRow(
                  props.t('production_cycle.start_date'),
                  productionCycle.startDate ? new Date(productionCycle.startDate).toLocaleDateString() : '-'
                )}
                {renderDetailRow(
                  props.t('production_cycle.end_date'),
                  productionCycle.endDate ? new Date(productionCycle.endDate).toLocaleDateString() : '-'
                )}
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <h6 className="mb-3 text-muted font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  {props.t('production_cycle.details')}
                </h6>
                {renderDetailRow(
                  props.t('production_cycle.dimension'),
                  productionCycle.dimension
                    ? `${formatNumber(productionCycle.dimension)} ${productionCycle.production?.unitDimension?.value ? `(${productionCycle.production.unitDimension.value})` : ''}`
                    : '-'
                )}
                {renderDetailRow(
                  props.t('production_cycle.previous_crop'),
                  productionCycle.previousCrop ? safeParseLabel(productionCycle.previousCrop) : '-'
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Informations liées : Projet / TPE / Production */}
        <Row className="mt-3">
          <Col sm="12">
            <Card className="shadow-sm">
              <CardBody>
                <h6 className="mb-3 text-muted font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  {props.t('production_cycle.relations')}
                </h6>
                <Row>
                  <Col md="4" className="mb-3 mb-md-0">
                    <div
                      className="rounded p-3 h-100"
                      style={{ backgroundColor: '#f0f4ff', border: '1px solid #d0d9f5' }}
                    >
                      <div className="mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {props.t('production_cycle.project')}
                      </div>
                      {productionCycle.tpe?.project?.id ? (
                        <Link
                          to={`${process.env.PUBLIC_URL}/project/detail/${productionCycle.tpe.project.id}`}
                          style={{ fontWeight: 600, color: '#3f51b5', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          {productionCycle.tpe.project.name || '-'}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      ) : (
                        <span style={{ fontWeight: 600 }}>-</span>
                      )}
                    </div>
                  </Col>
                  <Col md="4" className="mb-3 mb-md-0">
                    <div
                      className="rounded p-3 h-100"
                      style={{ backgroundColor: '#f0f4ff', border: '1px solid #d0d9f5' }}
                    >
                      <div className="mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {props.t('production_cycle.tpe')}
                      </div>
                      {productionCycle.tpe?.id ? (
                        <Link
                          to={`${process.env.PUBLIC_URL}/tpe/detail/${productionCycle.tpe.id}`}
                          style={{ fontWeight: 600, color: '#3f51b5', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          {productionCycle.tpe.name || '-'}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      ) : (
                        <span style={{ fontWeight: 600 }}>-</span>
                      )}
                    </div>
                  </Col>
                  <Col md="4">
                    <div
                      className="rounded p-3 h-100"
                      style={{ backgroundColor: '#f0f4ff', border: '1px solid #d0d9f5' }}
                    >
                      <div className="mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {props.t('production_cycle.production')}
                      </div>
                      <span style={{ fontWeight: 600, color: '#212529' }}>
                        {productionCycle.production?.name || '-'}
                      </span>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Pratiques */}
        <Row className="mt-3">
          <Col sm="12">
            <AccordionElement title={props.t('production_cycle.practices')}>
              <div style={{ padding: '16px 8px 8px' }}>
                <table className="table table-sm table-hover">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ fontWeight: 600 }}>{props.t('practice.name')}</th>
                      <th style={{ fontWeight: 600 }}>{props.t('practice.description')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productionCycle.pratiques?.length > 0 ? (
                      productionCycle.pratiques.map((p, index) => (
                        <tr key={index}>
                          <td style={{ fontWeight: 500 }}>{p.name}</td>
                          <td style={{ color: '#6c757d' }}>{p.description || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2}>
                          <Alert variant="outlined" severity="info" sx={{ mt: 1 }}>
                            {props.t('production_cycle.no_practices')}
                          </Alert>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </AccordionElement>
          </Col>
        </Row>

        {/* Productivité */}
        <Row className="mt-3">
          <Col sm="12">
            <AccordionElement title={props.t('production_cycle.productivity')}>
              <div style={{ padding: '16px 8px 8px' }}>
                <ProductivityTable
                  t={props.t}
                  productivites={productionCycle.productivites || []}
                  unitProductionName={productionCycle.production?.unitProduction?.value || ''}
                  showEditAction={false}
                />
              </div>
            </AccordionElement>
          </Col>
        </Row>

        {/* Charges */}
        <Row className="mt-3">
          <Col sm="12">
            <AccordionElement title={props.t('production_cycle.charges')}>
              <div style={{ padding: '16px 8px 8px' }}>
                <ProductionCycleChargesTable
                  t={props.t}
                  charges={productionCycle.charges || []}
                />
              </div>
            </AccordionElement>
          </Col>
        </Row>

        {/* Produits */}
        <Row className="mt-3 mb-4">
          <Col sm="12">
            <AccordionElement title={props.t('production_cycle.products')}>
              <div style={{ padding: '16px 8px 8px' }}>
                <ProductionCycleProductsTable
                  t={props.t}
                  produits={productionCycle.produits || []}
                />
              </div>
            </AccordionElement>
          </Col>
        </Row>

      </Container>
    </Fragment>
  );
};

export default translate(ProductionCycleDetailPage);
